import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  TextField,
  Stack,
  Box,
  Button,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { styleSheet } from "./style";
import { withStyles } from "@mui/styles";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { useSelector, useDispatch } from "react-redux";
import { updateAgentRosterProfile } from "../../../../../redux/agents/agentRoster/updateProfile";
import { updateBioStatus } from "../../../../../redux/agents/agentRoster/updateBioStatus";
import { toast } from "react-toastify";
import ConfirmModal from "../../../../../components/globalModal/ConfirmModal";
import moment from "moment";

// Constants
const BIO_TABS = {
  LONG: "long",
  SHORT: "short",
  CMA: "cma",
};

const SUBMIT_TYPES = {
  SUBMIT: "submit",
  SUBMIT_AND_APPROVE: "submitAndApprove",
  APPROVE: "approve",
  REJECT: "reject",
};

const BIO_STATUS = {
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
};

const BiographyDetails = ({ classes }) => {
  const toastId = useRef(null);
  const [isEditAble, setIsEditAble] = useState(false);
  const [tab, setTab] = useState(BIO_TABS.LONG);
  const [openApprove, setOpenApprove] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [submitType, setSubmitType] = useState("");

  const [bioDetail, setBioDetail] = useState({
    id: "",
    bio: "",
    short_bio: "",
    cma_bio: "",
    type: "bio",
  });

  const dispatch = useDispatch();

  // Memoized selectors
  const agentRosterProfile = useSelector(
    (state) => state.agentRoster.agentRosterProfile
  );
  const updateStatus = useSelector(
    (state) => state.agentRoster.agentRosterProfile?.updateStatus
  );
  const storedUsers = useSelector(
    (state) => state.users.storedListOfUsers?.list
  );
  const adminDetail = useSelector((state) => state.adminAuth?.adminDetail);

  const { profile, updateLoading } = agentRosterProfile;

  // Memoized values
  const isAdmin = useMemo(
    () => adminDetail?.role.name === "Admin",
    [adminDetail]
  );
  const bioStatus = useMemo(() => profile?.bio_status, [profile?.bio_status]);

  const approverName = useMemo(() => {
    if (!profile?.bio_approved_by || !storedUsers)
      return profile?.bio_approved_by;
    return (
      storedUsers.find((user) => user.id === profile.bio_approved_by)
        ?.full_name || profile.bio_approved_by
    );
  }, [profile?.bio_approved_by, storedUsers]);

  const rejectorName = useMemo(() => {
    if (!profile?.bio_rejected_by || !storedUsers)
      return profile?.bio_rejected_by;
    return (
      storedUsers.find((user) => user.id === profile.bio_rejected_by)
        ?.full_name || profile.bio_rejected_by
    );
  }, [profile?.bio_rejected_by, storedUsers]);

  // Initialize bio details when profile changes
  useEffect(() => {
    if (profile?.id && !bioDetail.id) {
      setBioDetail({
        id: profile.id,
        bio: profile.bio || "",
        short_bio: profile.short_bio || "",
        cma_bio: profile.cma_bio || "",
        type: "bio",
      });
    }
  }, [profile, bioDetail.id]);

  // Memoized toast handlers
  const showToast = useCallback((message, type = "success") => {
    if (!toast.isActive(toastId.current)) {
      toast[type](message, { position: toast.POSITION.TOP_RIGHT });
    }
  }, []);

  // Success handlers
  const handleSuccess = useCallback(() => {
    showToast("You have successfully update BIO");
    setIsEditAble(false);
  }, [showToast]);

  const handleError = useCallback(
    (error) => {
      showToast(JSON.stringify(error), "error");
    },
    [showToast]
  );

  const handleSuccessSubmit = useCallback(() => {
    showToast("You have successfully submitted BIO");
    setSubmitType("");
    setIsEditAble(false);
  }, [showToast]);

  const handleSuccessSubmitApprove = useCallback(() => {
    showToast("You have successfully submitted BIO and Approved the BIO");
    setSubmitType("");
    setIsEditAble(false);
    setOpenApprove(false);
  }, [showToast]);

  const handleSuccessSubmitReject = useCallback(() => {
    showToast("You have successfully rejected BIO");
    setSubmitType("");
    setIsEditAble(false);
    setOpenReject(false);
  }, [showToast]);

  const handleSuccessApprove = useCallback(() => {
    showToast("You have successfully approved BIO");
    setSubmitType("");
    setIsEditAble(false);
    setOpenApprove(false);
  }, [showToast]);

  // Field update handler
  const handleUpdateField = useCallback((field, value) => {
    setBioDetail((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Action handlers
  const handleSave = useCallback(() => {
    const { bio, short_bio, cma_bio } = bioDetail;
    if (bio || short_bio || cma_bio) {
      dispatch(
        updateAgentRosterProfile({ ...bioDetail, handleSuccess, handleError })
      );
    }
  }, [bioDetail, dispatch, handleSuccess, handleError]);

  const createBioStatusPayload = useCallback(
    (status, bio, additionalFields = {}) => ({
      id: profile.id,
      schema: {
        bio_status: status,
        bio,
        ...additionalFields,
      },
    }),
    [profile?.id]
  );

  const handleSubmit = useCallback(() => {
    setSubmitType(SUBMIT_TYPES.SUBMIT);
    const payload = {
      ...createBioStatusPayload(BIO_STATUS.SUBMITTED, bioDetail.bio),
      handleSuccess: handleSuccessSubmit,
      handleError,
    };
    dispatch(updateBioStatus(payload));
  }, [
    bioDetail.bio,
    createBioStatusPayload,
    handleSuccessSubmit,
    handleError,
    dispatch,
  ]);

  const handleSubmitAndApprove = useCallback(() => {
    setSubmitType(SUBMIT_TYPES.SUBMIT_AND_APPROVE);
    const payload = {
      ...createBioStatusPayload(BIO_STATUS.APPROVED, bioDetail.bio),
      handleSuccess: handleSuccessSubmitApprove,
      handleError,
    };
    dispatch(updateBioStatus(payload));
  }, [
    bioDetail.bio,
    createBioStatusPayload,
    handleSuccessSubmitApprove,
    handleError,
    dispatch,
  ]);

  const handleConfirmApprove = useCallback(() => {
    setOpenApprove(false);
    const payload = {
      ...createBioStatusPayload(BIO_STATUS.APPROVED, bioDetail.bio),
      handleSuccess: handleSuccessApprove,
      handleError,
    };
    dispatch(updateBioStatus(payload));
  }, [
    bioDetail.bio,
    createBioStatusPayload,
    handleSuccessApprove,
    handleError,
    dispatch,
  ]);

  const handleConfirmReject = useCallback(
    (reason) => {
      setOpenReject(false);
      const payload = {
        ...createBioStatusPayload(BIO_STATUS.REJECTED, bioDetail.bio, {
          bio_rejection_reason: reason,
        }),
        handleSuccess: handleSuccessSubmitReject,
        handleError,
      };
      dispatch(updateBioStatus(payload));
    },
    [
      bioDetail.bio,
      createBioStatusPayload,
      handleSuccessSubmitReject,
      handleError,
      dispatch,
    ]
  );

  // Tab configuration
  const tabConfig = useMemo(
    () => [
      { key: BIO_TABS.LONG, label: "Long Biography", field: "bio" },
      { key: BIO_TABS.SHORT, label: "Short Biography", field: "short_bio" },
      { key: BIO_TABS.CMA, label: "CMA Biography", field: "cma_bio" },
    ],
    []
  );

  const currentTabConfig = useMemo(
    () => tabConfig.find((t) => t.key === tab),
    [tabConfig, tab]
  );

  // Render approval/rejection info
  const renderStatusInfo = useCallback(() => {
    if (bioStatus === BIO_STATUS.APPROVED) {
      return (
        <Box sx={{ mb: "10px" }}>
          {profile?.bio_approved_by && (
            <Typography>
              <b>Approved by: </b>
              {approverName}
            </Typography>
          )}
          {profile?.bio_approved_at && (
            <Typography>
              <b>Approved at: </b>
              {moment(profile.bio_approved_at).format("llll")}
            </Typography>
          )}
        </Box>
      );
    }

    if (bioStatus === BIO_STATUS.REJECTED) {
      return (
        <Box sx={{ mb: "10px" }}>
          {profile?.bio_rejected_by && (
            <Typography>
              <b>Rejected by: </b>
              {rejectorName}
            </Typography>
          )}
          {profile?.bio_rejected_at && (
            <Typography>
              <b>Rejected at: </b>
              {moment(profile.bio_rejected_at).format("llll")}
            </Typography>
          )}
          {profile?.bio_rejection_reason && (
            <Typography>
              <b>Rejected reason: </b>
              {profile.bio_rejection_reason}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  }, [bioStatus, profile, approverName, rejectorName]);

  // Render action buttons for admin
  const renderAdminActionButtons = useCallback(() => {
    if (!isAdmin || bioStatus !== BIO_STATUS.SUBMITTED) return null;

    return (
      <Box
        className={classes.actionButtons}
        sx={{
          pointerEvents: isEditAble ? "auto" : "none",
          opacity: isEditAble ? 1 : 0.5,
        }}
      >
        <Button
          onClick={() => {
            setOpenApprove(true);
            setSubmitType(SUBMIT_TYPES.APPROVE);
          }}
          title="Sets status to Approved"
          variant="contained"
          color="success"
          startIcon={<CheckRoundedIcon />}
        >
          Approve
        </Button>
        <Button
          onClick={() => {
            setOpenReject(true);
            setSubmitType(SUBMIT_TYPES.REJECT);
          }}
          title="Sets status to Rejected"
          variant="contained"
          color="error"
          startIcon={<ClearRoundedIcon />}
        >
          Reject
        </Button>
      </Box>
    );
  }, [isAdmin, bioStatus, isEditAble, classes.actionButtons]);

  // Render bottom action buttons
  const renderBottomActions = useCallback(() => {
    if (!isEditAble) return null;

    const isLongBioTab = tab === BIO_TABS.LONG;
    const showSubmitButtons = isAdmin && bioStatus === null && isLongBioTab;

    if (showSubmitButtons) {
      return (
        <Box className={classes.actionButtons}>
          <LoadingButton
            onClick={handleSubmit}
            loading={
              updateStatus?.isLoading && submitType === SUBMIT_TYPES.SUBMIT
            }
            classes={{ root: classes.saveButTon }}
            color="inherit"
            autoFocus
            size="small"
            loadingPosition="start"
          >
            Submit
          </LoadingButton>
          <LoadingButton
            onClick={handleSubmitAndApprove}
            loading={
              updateStatus?.isLoading &&
              submitType === SUBMIT_TYPES.SUBMIT_AND_APPROVE
            }
            classes={{ root: classes.saveButTon }}
            color="inherit"
            autoFocus
            size="small"
            loadingPosition="start"
          >
            Submit & Approve
          </LoadingButton>
        </Box>
      );
    }

    return (
      <LoadingButton
        onClick={handleSave}
        loading={updateLoading === "bio"}
        classes={{ root: classes.saveButTon }}
        color="inherit"
        autoFocus
        size="small"
        loadingPosition="start"
      >
        Save
      </LoadingButton>
    );
  }, [
    isEditAble,
    tab,
    isAdmin,
    bioStatus,
    classes,
    handleSubmit,
    handleSubmitAndApprove,
    handleSave,
    updateStatus,
    submitType,
    updateLoading,
  ]);

  return (
    <Card variant="outlined" sx={{ backgroundColor: "white", mb: "16px" }}>
      <CardHeader
        classes={{ title: classes.pageHeading }}
        title="Biographies"
        action={
          <IconButton
            onClick={() => setIsEditAble(!isEditAble)}
            aria-label={isEditAble ? "unlock" : "locked"}
          >
            {isEditAble ? (
              <LockOpenIcon sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
            ) : (
              <LockIcon sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
            )}
          </IconButton>
        }
      />

      <Box className={classes.bioTabsSection}>
        <Box className={classes.bioTabs}>
          {tabConfig.map(({ key, label }) => (
            <Box
              key={key}
              onClick={() => setTab(key)}
              className={
                tab === key ? classes.bioTabsItemActive : classes.bioTabsItem
              }
            >
              {label}
            </Box>
          ))}
        </Box>
        <Box className={classes.tabsRightSection}></Box>
      </Box>

      <CardContent>
        {tab === BIO_TABS.LONG && renderAdminActionButtons()}
        {tab === BIO_TABS.LONG && renderStatusInfo()}

        <TextField
          fullWidth
          multiline
          inputProps={{ readOnly: !isEditAble }}
          rows={12}
          onChange={(e) =>
            handleUpdateField(currentTabConfig.field, e.target.value)
          }
          value={bioDetail[currentTabConfig.field] || ""}
          placeholder={`Enter ${currentTabConfig.label}`}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={{ backgroundColor: isEditAble ? "white" : "#f9f9f9" }}
        />

        {isEditAble && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            sx={{ mt: "20px" }}
          >
            {renderBottomActions()}
          </Stack>
        )}
      </CardContent>

      <ConfirmModal
        open={openApprove}
        setOpen={setOpenApprove}
        loading={updateStatus?.isLoading && submitType === SUBMIT_TYPES.APPROVE}
        title="Approve Confirmation"
        content="Are you sure you want to approve this biography?"
        handleConfirm={handleConfirmApprove}
      />

      <ConfirmModal
        open={openReject}
        setOpen={setOpenReject}
        loading={updateStatus?.isLoading && submitType === SUBMIT_TYPES.REJECT}
        title="Reject Confirmation"
        content="Are you sure you want to reject this biography? If so, please provide a reason for the rejection below."
        handleConfirm={handleConfirmReject}
        reasonRequired={true}
      />
    </Card>
  );
};

export default withStyles(styleSheet, {
  name: "agentRosterDetailsBiographyStyle",
})(BiographyDetails);
